//@ts-nocheck
import formElementMixin from "@banno/jha-wc/src/js/mixins/form-element-mixin";
import { LitElement, css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { GlobalVariables } from "../../../utils/constants";
import "@banno/jha-wc/src/jha-dialog/jha-dialog";
import "@banno/jha-wc/src/icons/jha-icon-circle-checkmark";
import "@banno/jha-wc/src/icons/jha-icon-circle-checkmark-filled";
import "@banno/jha-wc/src/forms/jha-form-radio-button/jha-form-radio-button";
import { openJhftDialog } from "../../../Components/JhftJhaDialog";

/** Sends the choice to the API. Resolves an error message to keep the form open, or null when done. */
export type ChangePasswordSubmit = (formData: ChangePasswordPopup["formData"]) => Promise<string | null>;

@customElement("change-password")
export class ChangePasswordPopup extends formElementMixin(LitElement) {
  constructor(userId: string, sshCheck: boolean, submit?: ChangePasswordSubmit) {
    super();
    this.userId = userId;
    this.sshCheck = sshCheck;
    this.submit = submit;
  }

  private submit?: ChangePasswordSubmit;

  /** Read by DialogUtil: ignore backdrop clicks so a stray click cannot drop a typed password. */
  hideCancel = true;

  private _closePromptOpen = false;
  private _forceClose = false;

  @property({ type: Number }) passwordMinLength = GlobalVariables.USER_PASSWORD_MIN_LENGTH;

  @state() formData = {
    checkPass: false,
    checkSsh: false,
    newPassword: "",
    confirmPassword: "",
    newSSHKey: "",
  };

  @state() public showPassword = false;
  @state() public showConfirmPassword = false;
  @state() public validSSHKey = false;
  @state() public saving = false;
  /** Server rejection, shown in the form so the admin can fix it without retyping. */
  @state() public submitError = "";

  // Password validation states
  @state() public atLeastMinLength = false;
  @state() public atLeastOneLetter = false;
  @state() public atLeastOneNumber = false;
  @state() public atLeastOneUpperCase = false;
  @state() public atLeastOneLowerCase = false;
  @state() public atLeastOneSpecial = false;
  @state() public cannotBeginOrEndInSpaces = false;
  @state() public newPassAndConfirmPassAreSame = false;

  @property({ type: Boolean }) setPass = false;
  @property({ type: Boolean }) setSsh = false;
  @property() userId = "";

  static readonly styles = css`
    p > [icon] {
      margin-left: 10px;
      margin-right: 8px;
      vertical-align: middle;
    }
    jha-icon-circle-checkmark-filled {
      fill: var(--jha-color-success);
      margin-left: 10px;
    }
    jha-form-input {
      margin-top: 20px;
      margin-right: 8px;
      margin-left: 8px;
    }
    h3 {
      font-size: 16px;
      font-style: normal;
      font-weight: 700;
      line-height: 24px;
      color: #06185f;
    }

    #passTitle {
      font-size: 12px;
      margin-top: 20px;
      margin-left: 10px;
    }
    #btnDiv {
      display: flex;
      justify-content: center;
      margin-top: 30px;
      gap: 20px;
    }
    #dialogBtns {
      display: flex;
      gap: 15px;
      justify-content: center;
    }
    #confirmPassword {
      margin-top: 20px;
    }
    .submit-error {
      color: var(--jha-color-danger, #d32f2f);
      margin: 16px 8px 0;
      text-align: center;
      white-space: pre-wrap;
    }
    .password-requirements {
      margin-bottom: 0.5em;
      margin-top: 0.5em;
    }
  `;

  public resetAllRequirementStates() {
    this.atLeastMinLength = false;
    this.atLeastOneLetter = false;
    this.atLeastOneNumber = false;
    this.atLeastOneUpperCase = false;
    this.atLeastOneLowerCase = false;
    this.atLeastOneSpecial = false;
    this.cannotBeginOrEndInSpaces = false;
    this.newPassAndConfirmPassAreSame = false;
  }

  private allPasswordValidationsPass(): boolean {
    return [
      this.atLeastMinLength,
      this.atLeastOneLetter,
      this.atLeastOneNumber,
      this.atLeastOneUpperCase,
      this.atLeastOneLowerCase,
      this.atLeastOneSpecial,
      this.cannotBeginOrEndInSpaces,
      this.newPassAndConfirmPassAreSame,
    ].every(Boolean);
  }

  public checkSetPassword(e: CustomEvent) {
    const target = e.target as HTMLInputElement | null;
    this.setPass = !!target && target.value === "setPass";
    // The inputs re-render empty when the option comes back, so drop what was typed.
    this.formData = { ...this.formData, checkPass: this.setPass, newPassword: "", confirmPassword: "" };
    this.resetAllRequirementStates();
    this.submitError = "";
  }

  public checkSetSsh(e: CustomEvent) {
    const target = e.target as HTMLInputElement | null;
    this.setSsh = !!target && target.value === "setSsh";
    this.formData = { ...this.formData, checkSsh: this.setSsh, newSSHKey: "" };
    this.validSSHKey = false;
    this.submitError = "";
  }

  /** Sending the reset email is always allowed; setting a credential now needs a valid one. */
  get saveDisabled(): boolean {
    if (this.saving) return true;
    if (this.sshCheck) return this.formData.checkSsh && !this.validSSHKey;
    return this.formData.checkPass && !this.allPasswordValidationsPass();
  }

  /** Only typed input is worth a prompt; picking an option alone loses nothing. */
  hasUnsavedChanges(): boolean {
    const f = this.formData;
    if (this.sshCheck) return f.checkSsh && f.newSSHKey !== "";
    return f.checkPass && (f.newPassword !== "" || f.confirmPassword !== "");
  }

  public onNewPasswordChange(e: CustomEvent) {
    const newPass = (e.target && (e.target as HTMLInputElement).value) ?? "";
    this.resetAllRequirementStates();

    this.atLeastMinLength = newPass.length >= this.passwordMinLength;
    this.atLeastOneLetter = /[a-zA-Z]/.test(newPass);
    this.atLeastOneNumber = /[0-9]/.test(newPass);
    this.atLeastOneUpperCase = /[A-Z]/.test(newPass);
    this.atLeastOneLowerCase = /[a-z]/.test(newPass);
    this.atLeastOneSpecial = /[^a-zA-Z0-9]/.test(newPass);
    this.cannotBeginOrEndInSpaces = newPass.length > 0 && !/^\s|\s$/.test(newPass);
    this.newPassAndConfirmPassAreSame = this.passwordsMatch(newPass, this.formData.confirmPassword);
    this.submitError = "";
    this.formData = { ...this.formData, newPassword: newPass };
  }

  public onSSHKeyChange(e: CustomEvent) {
    const value = (e.target && (e.target as HTMLInputElement).value) ?? "";
    const sshRsaRegex = /^ssh-rsa\s+([A-Za-z0-9+/=]+)(\s+\S+)?$/;

    this.validSSHKey = sshRsaRegex.test(value);
    this.submitError = "";
    this.formData = { ...this.formData, newSSHKey: value };
  }

  public onConfirmPasswordChange(e: CustomEvent) {
    const target = e.target as HTMLInputElement | null;
    const confirmPass = target?.value ?? "";
    this.newPassAndConfirmPassAreSame = this.passwordsMatch(this.formData.newPassword, confirmPass);
    this.submitError = "";
    this.formData = { ...this.formData, confirmPassword: confirmPass };
  }

  private passwordsMatch(newPassword: string, confirmPassword: string): boolean {
    const trimmedNew = newPassword.trim();
    const trimmedConfirm = confirmPassword.trim();
    return trimmedNew.length > 0 && trimmedConfirm.length > 0 && trimmedNew === trimmedConfirm;
  }

  public async onSaveClick(e?: Event) {
    if (e) {
      e.preventDefault();
    }
    if (this.saveDisabled) return;
    const dialog = this.shadowRoot?.querySelector("jha-dialog") as any;
    const data = { ...this.formData };

    if (this.submit) {
      this.saving = true;
      this.submitError = "";
      try {
        const error = await this.submit(data);
        if (error) {
          this.submitError = error;
          return;
        }
      } catch {
        this.submitError = "Something went wrong, please try again.";
        return;
      } finally {
        this.saving = false;
      }
    }
    // X/Esc can force-close while this request is in flight. Drop the success close
    // so a Leave does not also confirm a dialog that is already gone.
    if (this._forceClose || !this.isConnected) return;
    this._forceClose = true;
    dialog?.dispatchDialogConfirm(data);
  }

  connectedCallback() {
    super.connectedCallback();
    this._forceClose = false;
    this._closePromptOpen = false;
    document.addEventListener("cancel", this.handleEscCancel, true);
  }

  firstUpdated() {
    this.shadowRoot?.querySelector("jha-dialog")?.addEventListener("cancel", this.handleCloseCancel, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("cancel", this.handleEscCancel, true);
  }

  /** Header X and the Cancel button. DialogUtil closes on this event without asking, so gate it first. */
  private handleCloseCancel = async (e: Event) => {
    if (this._forceClose) return;
    // Save already left. Closing now would still apply the credential and toast after the popup is gone.
    if (this.saving) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    if (!this._closePromptOpen && !this.hasUnsavedChanges()) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (this._closePromptOpen) return;
    if (await this.confirmLeave()) this.forceClose();
  };

  /** Esc fires the native `cancel` on DialogUtil's host `<dialog>`. */
  private handleEscCancel = async (e: Event) => {
    if (this._forceClose) return;
    const hostDialog = this.closest("dialog");
    if (!hostDialog || e.target !== hostDialog) return;
    if (this.saving) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (!this._closePromptOpen && !this.hasUnsavedChanges()) return;
    e.preventDefault();
    e.stopPropagation();
    if (this._closePromptOpen) return;
    if (await this.confirmLeave()) this.forceClose();
  };

  private async confirmLeave(): Promise<boolean> {
    this._closePromptOpen = true;
    try {
      const result = await openJhftDialog({
        variant: "warning",
        title: "Unsaved Changes",
        message: "You have unsaved changes. Are you sure you want to leave without saving?",
        cancelLabel: "Stay",
        confirmLabel: "Leave Without Saving",
      });
      return result === "confirm";
    } finally {
      this._closePromptOpen = false;
    }
  }

  private forceClose() {
    this._forceClose = true;
    (this.shadowRoot?.querySelector("jha-dialog") as any)?.dispatchDialogEvent?.("cancel");
  }

  public onCancelClick(e?: Event) {
    if (e) {
      e.preventDefault();
    }
    const dialog = this.shadowRoot?.querySelector("jha-dialog");
    dialog?.shadowRoot?.querySelector("jha-icon-close")?.click();
  }

  public onNewPasswordIconClick() {
    this.showPassword = !this.showPassword;
  }

  public onConfirmPasswordIconClick() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  render() {
    return html`
      <jha-dialog title=${this.sshCheck ? "Change SSH Key" : "Change Password"} hide-confirm>
        <article slot="dialog-content">
          <form>
            <div>
              ${this.sshCheck ? this.renderSshSection() : this.renderPasswordSection()}
              ${this.submitError
        ? html`<p class="submit-error" role="alert" data-auto-id="submitError">${this.submitError}</p>`
        : undefined}
              ${this.renderDialogButtons()}
            </div>
          </form>
        </article>
      </jha-dialog>
    `;
  }

  private renderPasswordSection() {
    return html`
      <jha-form-radio-button
        name="change-password-radio"
        for="opt1"
        id="opt1"
        data-id="checkReset"
        data-auto-id="resetRadio"
        checked
        @checked=${this.checkSetPassword}>
        Send an email to the user with a link and instructions to reset the password.
      </jha-form-radio-button>
      <jha-form-radio-button
        name="change-password-radio"
        value="setPass"
        data-id="checkPass"
        data-auto-id="setPassRadio"
        @checked=${this.checkSetPassword}>
        Set password now
      </jha-form-radio-button>
      ${this.setPass ? this.renderPasswordInputs() : undefined}
    `;
  }

  private renderPasswordInputs() {
    return html`
      <jha-form-input
        data-auto-id="newPasswordInput"
        label="New Password"
        required
        warning="Password required"
        outline
        data-id="newPassword"
        @change=${this.onNewPasswordChange}
        maxlength="60"
        inputSymbol="true"
        type=${this.showPassword ? "text" : "password"}
        style="--jha-input-symbol-right-width: 28px;">
        <span slot="input-right">
          <jha-icon-show
            icon
            @click=${this.onNewPasswordIconClick}
            style="cursor: pointer;"
            title="Click to show/hide password">
          </jha-icon-show>
        </span>
      </jha-form-input>
      <jha-form-input
        data-auto-id="confirmPasswordInput"
        label="Confirm New Password"
        required
        id="confirmPassword"
        warning="Confirm New Password Required"
        data-id="confirmPassword"
        @change=${this.onConfirmPasswordChange}
        outline
        inputSymbolright="true"
        type=${this.showConfirmPassword ? "text" : "password"}
        style="--jha-input-symbol-right-width: 28px;">
        <span slot="input-right">
          <jha-icon-show
            icon
            @click=${this.onConfirmPasswordIconClick}
            style="cursor: pointer;"
            title="Click to show/hide password">
          </jha-icon-show>
        </span>
      </jha-form-input>
      ${this.renderPasswordRequirements()}
    `;
  }

  private renderPasswordRequirements() {
    return html`
      <div id="disabled">
        <p id="passTitle">Password Requirements:</p>
        <p class="password-requirements">
          ${this.atLeastMinLength
        ? html`<jha-icon-circle-checkmark-filled icon></jha-icon-circle-checkmark-filled>`
        : html`<jha-icon-circle-checkmark icon></jha-icon-circle-checkmark>`}
          Must be at least ${this.passwordMinLength} characters.
        </p>
        <p class="password-requirements">
          ${this.atLeastOneLetter
        ? html`<jha-icon-circle-checkmark-filled icon></jha-icon-circle-checkmark-filled>`
        : html`<jha-icon-circle-checkmark icon></jha-icon-circle-checkmark>`}
          Must contain at least one letter.
        </p>
        <p class="password-requirements">
          ${this.atLeastOneNumber
        ? html`<jha-icon-circle-checkmark-filled icon></jha-icon-circle-checkmark-filled>`
        : html`<jha-icon-circle-checkmark icon></jha-icon-circle-checkmark>`}
          Must contain at least one number.
        </p>
        <p class="password-requirements">
          ${this.atLeastOneUpperCase
        ? html`<jha-icon-circle-checkmark-filled icon></jha-icon-circle-checkmark-filled>`
        : html`<jha-icon-circle-checkmark icon></jha-icon-circle-checkmark>`}
          Must contain at least one uppercase letter.
        </p>
        <p class="password-requirements">
          ${this.atLeastOneLowerCase
        ? html`<jha-icon-circle-checkmark-filled icon></jha-icon-circle-checkmark-filled>`
        : html`<jha-icon-circle-checkmark icon></jha-icon-circle-checkmark>`}
          Must contain at least one lowercase letter.
        </p>
        <p class="password-requirements">
          ${this.atLeastOneSpecial
        ? html`<jha-icon-circle-checkmark-filled icon></jha-icon-circle-checkmark-filled>`
        : html`<jha-icon-circle-checkmark icon></jha-icon-circle-checkmark>`}
          Must contain at least one special character.
        </p>
        <p class="password-requirements">
          ${this.cannotBeginOrEndInSpaces
        ? html`<jha-icon-circle-checkmark-filled icon></jha-icon-circle-checkmark-filled>`
        : html`<jha-icon-circle-checkmark icon></jha-icon-circle-checkmark>`}
          Cannot begin or end with a space.
        </p>
        <p class="password-requirements">
          ${this.newPassAndConfirmPassAreSame
        ? html`<jha-icon-circle-checkmark-filled icon></jha-icon-circle-checkmark-filled>`
        : html`<jha-icon-circle-checkmark icon></jha-icon-circle-checkmark>`}
          New Password must be the same as Confirm Password.
        </p>
      </div>
    `;
  }

  private renderSshSection() {
    return html`
      <jha-form-radio-button
        name="change-ssh-radio"
        for="opt1"
        id="opt1"
        data-id="checkReset"
        data-auto-id="resetRadio"
        checked
        @checked=${this.checkSetSsh}>
        Send an email to the user with a link and instructions to reset the SSH key.
      </jha-form-radio-button>
      <jha-form-radio-button
        name="change-ssh-radio"
        value="setSsh"
        data-id="checkSsh"
        data-auto-id="setSshRadio"
        @checked=${this.checkSetSsh}>
        Set SSH Key now
      </jha-form-radio-button>
      ${this.setSsh
        ? html`
            <jha-form-textarea-input
              placeholder="SSH Key"
              warning=${this.formData.newSSHKey
            ? "Not a valid key. Paste an ssh-rsa public key: ssh-rsa AAAA... with an optional comment."
            : "SSH Key required."}
              type="text"
              role="textbox"
              label="SSH Key"
              data-id="newSSHKey"
              data-auto-id="SSHKeyInput"
              outline
              ?showWarning=${!this.validSSHKey}
              required
              @change=${this.onSSHKeyChange}
              style="margin-top: 20px;">
            </jha-form-textarea-input>
          `
        : undefined}
    `;
  }

  private renderDialogButtons() {
    return html`
      <div id="dialogBtns" style="margin-top: 20px;">
        <jha-button
          data-auto-id="saveBtn"
          sync
          ?disabled=${this.saveDisabled}
          ?loading=${this.saving}
          @click=${this.onSaveClick}>
          Save
        </jha-button>
        <jha-button data-auto-id="cancelBtn" sync tertiary ?disabled=${this.saving} @click=${this.onCancelClick}>
          Cancel
        </jha-button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "change-password": ChangePasswordPopup;
  }
}
